import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppNavigation, useSlider, useScrollReveal } from '../../hooks';
import { REVIEWS } from '../../constants';
import { Button } from '../ui';
import './CustomerReviews.css';

interface Review {
	id: string | number;
	name: string;
	location: string;
	rating: number;
	review: string;
	event?: string;
}

/**
 * Customer reviews carousel component
 * Fetches reviews from API, falls back to constants
 */
const CustomerReviews: React.FC = () => {
	const { t } = useTranslation();
	const { goToBookNow } = useAppNavigation();
	const [reviews, setReviews] = useState<Review[]>(REVIEWS);
	const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.1 });

	// Fetch reviews from API
	useEffect(() => {
		const fetchReviews = async () => {
			try {
				const response = await fetch('/api/reviews');
				const result = await response.json();
				if (result.success && result.reviews?.length > 0) {
					setReviews(result.reviews);
				}
			} catch {
				// Keep using default reviews
			}
		};
		fetchReviews();
	}, []);

	const { currentSlide, nextSlide, prevSlide, goToSlide } = useSlider({
		totalSlides: reviews.length,
		autoPlay: true,
		intervalMs: 5000,
	});

	const review = reviews[currentSlide] || reviews[0];

	return (
		<section className="customer-reviews" id="reviews" ref={sectionRef as React.RefObject<HTMLElement>}>
			<div className={`reviews-container ${isVisible ? 'visible' : ''}`}>
				{/* Header */}
				<div className="section-header">
					<h2>{t('reviews.title', 'Customer Reviews')}</h2>
					<p className="section-subtitle">
						{t('reviews.subtitle', 'See what our customers are saying about their Happy Hibachi experience')}
					</p>
				</div>

				{/* Review Card */}
				<div className="review-card" key={review.id}>
					{/* Stars */}
					<div className="review-stars">
						{[...Array(5)].map((_, i) => (
							<span key={i} className={`star ${i < review.rating ? 'filled' : ''}`}>
								★
							</span>
						))}
					</div>

					{/* Review Text */}
					<p className="review-text">&ldquo;{review.review}&rdquo;</p>

					{/* Reviewer Info */}
					<div className="reviewer-info">
						<div className="reviewer-avatar">
							{review.name.charAt(0)}
						</div>
						<div className="reviewer-details">
							<span className="reviewer-name">{review.name}</span>
							<span className="reviewer-location">{review.location}</span>
						</div>
					</div>
				</div>

				{/* Navigation */}
				<div className="review-nav">
					<button
						className="review-btn"
						onClick={prevSlide}
						type="button"
						aria-label={t('common.previous', 'Previous')}
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
							<path
								d="M15 18L9 12L15 6"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<button
						className="review-btn"
						onClick={nextSlide}
						type="button"
						aria-label={t('common.next', 'Next')}
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
							<path
								d="M9 18L15 12L9 6"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
				</div>

				{/* Dots */}
				<div className="review-dots" role="tablist" aria-label="Review slides">
					{reviews.map((_, index) => (
						<button
							key={index}
							className={`review-dot ${index === currentSlide ? 'active' : ''}`}
							onClick={() => goToSlide(index)}
							type="button"
							role="tab"
							aria-selected={index === currentSlide}
							aria-label={`${t('common.goTo', 'Go to')} ${index + 1}`}
						/>
					))}
				</div>

				{/* Trust Badge */}
				<div className="trust-badge">
					<div className="trust-badge-item">
						<span className="icon">⭐</span>
						<span>{t('reviews.rating', '5.0 Average Rating')}</span>
					</div>
					<div className="trust-badge-item">
						<span className="icon">✓</span>
						<span>{t('reviews.verified', '100+ Verified Reviews')}</span>
					</div>
					<div className="trust-badge-item">
						<span className="icon">🏆</span>
						<span>{t('reviews.topRated', 'Top Rated Service')}</span>
					</div>
				</div>

				{/* CTA */}
				<div className="reviews-cta">
					<h3>{t('reviews.ctaTitle', 'Ready to Create Your Own Amazing Experience?')}</h3>
					<p>{t('reviews.ctaText', 'Join hundreds of satisfied customers')}</p>
					<Button variant="primary" size="lg" onClick={goToBookNow} className="animate-glow">
						{t('nav.bookNow')}
					</Button>
				</div>
			</div>
		</section>
	);
};

export default CustomerReviews;
